/**
 * The tenancy contract (PLAN §11, D13, D14): who the caller is, which tenant
 * they act in, and the tenant-admin surface — users, groups, memberships — plus
 * the vendor's tenant registry. The routes are `tenancyRoutes.ts`, part of the
 * one route table (`routes.ts`).
 *
 * Every request and response body is `.strict()` (CLAUDE.md → the contract),
 * and every id embedded in a DynamoDB key is constrained to a character set
 * without `#`, the key separator, so an id can never forge a key segment
 * (`docs/data-model.md`).
 *
 * **The tenant never appears in a request body.** It comes only from the
 * verified token (D13). `tenantId` in `createTenantRequestSchema` is the id of
 * the tenant being CREATED by the vendor, not the tenant the caller acts in.
 *
 * A group's scope is the domain's `GrantScope` (`@acp/domain/access.js`), and
 * a caller's grants are the domain's `PermissionGrant[]`, so the one pure
 * `isPermitted` check runs in the cloud authorizer and on the node alike.
 */
import type { GrantScope, PermissionGrant } from '@acp/domain/access.js';
import { z } from 'zod';
import { permissionIdSchema, PERMISSION_IDS } from './permissions.js';

/**
 * A tenant id is chosen by the vendor at creation and never changes. It is a
 * key segment (`T#<tenantId>#…`), hence the strict alphabet.
 */
export const tenantIdSchema = z
  .string()
  .regex(/^[a-z][a-z0-9-]{2,39}$/, 'tenant ids are 3-40 lowercase letters, digits or hyphens, starting with a letter');

/** Group, site and studio ids: a key segment, so no `#` and no whitespace. */
export const entityIdSchema = z.string().regex(/^[A-Za-z0-9][A-Za-z0-9_-]{0,63}$/, 'ids are 1-64 letters, digits, hyphens or underscores');

/** A Cognito `sub`: a UUID. */
export const userIdSchema = z.string().regex(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/, 'user ids are Cognito subs (UUIDs)');

export const emailSchema = z.email().max(254);

/**
 * Where a group's permissions apply (D14): the whole tenant, or named sites
 * and studios — the domain's `GrantScope`.
 */
export const grantScopeSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('tenant') }).strict(),
  z
    .object({
      kind: z.literal('resources'),
      siteIds: z.array(entityIdSchema).max(200),
      studioIds: z.array(entityIdSchema).max(500),
    })
    .strict(),
]);

const permissionListSchema = z.array(permissionIdSchema).max(PERMISSION_IDS.length);

/** One group's contribution to a caller — the domain's `PermissionGrant`, with known ids. */
export const permissionGrantSchema = z.object({ permissions: permissionListSchema, scope: grantScopeSchema }).strict();

// A parsed contract value must BE a domain value, or the shared check would be
// checking something else. Compile-time proofs: these lines stop compiling if
// either shape drifts from `@acp/domain/access.js`.
const scopeFitsDomain: z.infer<typeof grantScopeSchema> extends GrantScope ? true : false = true;
const grantFitsDomain: z.infer<typeof permissionGrantSchema> extends PermissionGrant ? true : false = true;
const domainKindsCovered: GrantScope['kind'] extends z.infer<typeof grantScopeSchema>['kind'] ? true : false = true;
export const DOMAIN_SHAPE_PROOFS = [scopeFitsDomain, grantFitsDomain, domainKindsCovered] as const;

const groupNameSchema = z.string().trim().min(1).max(100);

// ─── resources ───

export const tenantSchema = z.object({ tenantId: tenantIdSchema, name: z.string(), createdAt: z.string() }).strict();
export type Tenant = z.infer<typeof tenantSchema>;

export const userSchema = z.object({ userId: userIdSchema, email: emailSchema, createdAt: z.string() }).strict();
export type User = z.infer<typeof userSchema>;

export const groupSchema = z
  .object({
    groupId: entityIdSchema,
    name: z.string(),
    permissions: permissionListSchema,
    scope: grantScopeSchema,
    createdAt: z.string(),
    updatedAt: z.string(),
  })
  .strict();
export type Group = z.infer<typeof groupSchema>;

// ─── path parameters ───

export const userPathSchema = z.object({ userId: userIdSchema.describe('The user’s id (their Cognito `sub`).') }).strict();
export const groupPathSchema = z.object({ groupId: entityIdSchema.describe('The group’s id.') }).strict();
export const memberPathSchema = groupPathSchema.extend({ userId: userIdSchema.describe('The member’s user id.') }).strict();

// ─── requests ───

export const createTenantRequestSchema = z
  .object({
    tenantId: tenantIdSchema,
    name: z.string().trim().min(1).max(200),
    /** The tenant's first admin: created in Cognito (invitation suppressed) and put in the Admin group. */
    adminEmail: emailSchema,
  })
  .strict();
export type CreateTenantRequest = z.infer<typeof createTenantRequestSchema>;

export const createUserRequestSchema = z
  .object({
    email: emailSchema,
    /** Groups to add the new user to; each must exist in the caller's tenant. */
    groupIds: z.array(entityIdSchema).max(50).optional(),
  })
  .strict();
export type CreateUserRequest = z.infer<typeof createUserRequestSchema>;

export const createGroupRequestSchema = z.object({ name: groupNameSchema, permissions: permissionListSchema, scope: grantScopeSchema }).strict();
export type CreateGroupRequest = z.infer<typeof createGroupRequestSchema>;

export const updateGroupRequestSchema = z
  .object({ name: groupNameSchema.optional(), permissions: permissionListSchema.optional(), scope: grantScopeSchema.optional() })
  .strict()
  .refine((body) => body.name !== undefined || body.permissions !== undefined || body.scope !== undefined, {
    message: 'a group update must change at least one of name, permissions, scope',
  });
export type UpdateGroupRequest = z.infer<typeof updateGroupRequestSchema>;

// ─── responses ───

export const meResponseSchema = z
  .object({
    user: z.object({ userId: userIdSchema, email: emailSchema }).strict(),
    tenant: z.object({ tenantId: tenantIdSchema, name: z.string() }).strict(),
    /** Every permission held somewhere in the tenant, sorted. */
    permissions: z.array(permissionIdSchema),
    /** Where each applies: one entry per group, as the authorizer resolved them (D14). */
    grants: z.array(permissionGrantSchema),
  })
  .strict();
export type MeResponse = z.infer<typeof meResponseSchema>;

export const createdTenantResponseSchema = z.object({ tenant: tenantSchema, adminUser: userSchema }).strict();
export const listTenantsResponseSchema = z.object({ tenants: z.array(tenantSchema) }).strict();
export const userResponseSchema = z.object({ user: userSchema }).strict();
export const listUsersResponseSchema = z.object({ users: z.array(userSchema) }).strict();
export const groupResponseSchema = z.object({ group: groupSchema }).strict();
export const groupWithMembersResponseSchema = z.object({ group: groupSchema, memberIds: z.array(userIdSchema) }).strict();
export const listGroupsResponseSchema = z.object({ groups: z.array(groupSchema) }).strict();
